import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Modifier, usePopper } from "react-popper"
import { VirtualElement, createPopper, Options } from "@popperjs/core"

type usePopperFn = typeof usePopper

const useTogglePopper = <Modifiers>(
	referenceElement?: Element | VirtualElement | null,
	popperElement?: HTMLElement | null,
	options?: Omit<Partial<Options>, "modifiers"> & {
		createPopper?: typeof createPopper
		modifiers?: ReadonlyArray<Modifier<Modifiers>>
	},
	lockVisible: boolean = false
): ReturnType<usePopperFn> & {
	visible: boolean
	setVisible: Dispatch<SetStateAction<boolean>>
} => {
	if (referenceElement && !(referenceElement instanceof Element)) {
		throw new Error("useTogglePopper cannot be used with virtual elements.")
	}

	const [visible, setVisible] = useState<boolean>(false)
	const ret = usePopper(referenceElement, popperElement, options)

	useEffect(() => {
		if (!referenceElement || !popperElement || lockVisible) {
			return
		}

		const fn = (event: MouseEvent | TouchEvent) => {
			const { target } = event

			if (!target || !(target instanceof Element)) {
				return
			}

			if (
				referenceElement.contains(target) ||
				popperElement.contains(target)
			) {
				return
			}

			setVisible(false)
		}

		document.documentElement.addEventListener("mouseup", fn)
		document.documentElement.addEventListener("touchend", fn)

		return () => {
			document.documentElement.removeEventListener("mouseup", fn)
			document.documentElement.removeEventListener("touchend", fn)
		}
	}, [referenceElement, popperElement, lockVisible])

	return {
		...ret,
		visible,
		setVisible,
	}
}

export default useTogglePopper
